import { NgModule, isDevMode, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AppRoutingModule, routes } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import {
  MAT_RADIO_DEFAULT_OPTIONS,
  MatRadioModule,
} from '@angular/material/radio';
import { MatSortModule } from '@angular/material/sort';

import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatStepperModule } from '@angular/material/stepper';

import {
  AddReferenceCardComponent,
  BreadCrumbsComponent,
  ButtonComponent,
  FormContainerComponent,
  FormHeaderComponent,
  HeaderComponent,
  DropdownComponent,
  RadioOptionsComponent,
  TextInputComponent,
  AlertComponent,
} from './shared/components';

import {
  BasicInformationComponent,
  ItemVendorComponent,
  ReferenceComponent,
  AttachmentComponent,
  AddItemPageComponent,
} from './modules/mypurchase-form';
import { LandingPageComponent } from './modules/purchase-modes/purchase-modes.component';

import {
  OptionBoxComponent,
  OptionsSectionComponent,
} from './modules/purchase-modes';

import { FormStatusComponent } from './shared/components/layout/form-header';

import { AuthModule } from './auth/auth.module';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDividerModule } from '@angular/material/divider';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { StoreModule } from '@ngrx/store';
import { formReducer } from './core/store/form.reducer';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { DialogComponent } from './shared/components/dialog/dialog.component';
import { BackendApiService } from './core/services/backend-api';
import { SAPAPI } from './core/services/sap-api';
import { ApprovingOfficerPageComponent } from './modules/approving-officer-page/approving-officer-page.component';
import { AoOverviewComponent } from './modules/approving-officer-page/approving-overview/approving-overview.component';
import { MyDocumentsListingComponent } from './modules/my-documents/my-documents-listing/my-documents-listing.component';
import { MyDocumentsComponent } from './modules/my-documents/my-documents.component';
import { WorklistPageComponent } from './modules/my-worklist/my-worklist.component';
import { MyWorklistComponent } from './modules/my-worklist/my-worklist-listing/my-worklist-listing.component';
import { WorkflowHistoryPageComponent } from './modules/shared/workflow-history/workflow-history.component';
import { CurrentWorkflowComponent } from './modules/shared/current-workflow/current-workflow.component';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { LoaderDirective } from 'src/app/directives/loader.directive';
import { ReadOnlyTableComponent } from './shared/components/read-only-table/read-only-table.component';
import { InfoDialogComponent } from './shared/components/info-dialog/info-dialog.component';
import { ShareDataService } from './shared/services/share-data.service';
import { dropdownReducer } from './core/store/dropDown.reducer';
import { NgxFileDropModule } from 'ngx-file-drop';
import { InputDialogComponent } from './shared/components/input-dialog/input-dialog.component';
import { AoActionDialogComponent } from './modules/approving-officer-page/approving-action-dialog/approving-action-dialog.component';
import { ForwardDialogComponent } from './shared/components/forward-dialog/forward-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormTransformerPipe } from './shared/pipes/form-transformer/form-transformer.pipe';
import { userReducer } from './core/store/UserInfo.reducer';
import { taggingReducer } from './core/store/TaggedVendors.reducer';
import { BudgetSummaryComponent } from './modules/mypurchase-form/item-vendor/budget-summary/budget-summary.component';
import { AddVendorComponent } from './modules/mypurchase-form/item-vendor/add-vendor/add-vendor.component';
import { VendorSearchDialogComponent } from './modules/mypurchase-form/item-vendor/add-vendor/vendor-search-dialog/vendor-search-dialog.component';
import { ItemListComponent } from './modules/mypurchase-form/item-vendor/item-list/item-list.component';
import { ItemInfoComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/item-info/item-info.component';
import { ItemPriceComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/item-price/item-price.component';
import { ItemBudgetComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/item-budget/item-budget.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { AddBudgetComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/item-budget/add-budget/add-budget.component';
import { BudgetListingTableComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/item-budget/budget-listing-table/budget-listing-table.component';
import { OutputHistoryComponent } from './modules/shared/output-history/output-history.component';
import { AddendumDialogComponent } from './shared/components/addendum-dialog/addendum-dialog.component';
import { EmailDialogComponent } from './shared/components/email-dialog/email-dialog.component';
import { AslVendorSearchDialogComponent } from './modules/mypurchase-form/item-vendor/add-vendor/asl-vendor-search-dialog/asl-vendor-search-dialog.component';
import { EditVendorDialogComponent } from './modules/mypurchase-form/item-vendor/add-vendor/edit-vendor-dialog/edit-vendor-dialog.component';
import { ItemAddOnComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/item-add-on/item-add-on.component';
import { AddOnFormComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/item-add-on/add-on-form/add-on-form.component';
import { AddOnFeatureComponent } from './modules/mypurchase-form/add-on-feature/add-on-feature.component';
import { ExtendedWarrantyComponent } from './modules/mypurchase-form/add-on-feature/extended-warranty/extended-warranty.component';
import { MaintenanceComponent } from './modules/mypurchase-form/add-on-feature/maintenance/maintenance.component';
import { OptionToPurchaseComponent } from './modules/mypurchase-form/add-on-feature/option-to-purchase/option-to-purchase.component';
import { TenderItemComponent } from './modules/mypurchase-form/item-vendor/item-list/tender-item/tender-item.component';
import { AddNewOptionPurchaseComponent } from './modules/mypurchase-form/add-on-feature/option-to-purchase/add-new-option-purchase/add-new-option-purchase.component';
import { TenderDateTimeFormComponent } from './modules/mypurchase-form/item-vendor/add-vendor/tender-date-time-form/tender-date-time-form.component';
import { InputJustificationComponent } from './modules/mypurchase-form/item-vendor/add-vendor/input-justification/input-justification.component';
import { TenderDocumentsComponent } from './modules/approving-officer-page/approving-overview/tender-documents/tender-documents.component';
import { OptionPurchaseTableComponent } from './modules/approving-officer-page/approving-overview/option-purchase-table/option-purchase-table.component';
import { ScopeOfWorkComponent } from './modules/approving-officer-page/approving-overview/scope-of-work/scope-of-work.component';
import { CeDialogComponent } from './shared/components/ce-dialog/ce-dialog.component';
import { WorkflowTableComponent } from './modules/shared/current-workflow/workflow-table/workflow-table.component';
import { CoveringOfficerChipComponent } from './shared/components/covering-officer-chip/covering-officer-chip.component';
import { EditableTableComponent } from './shared/components/editable-table/editable-table.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { ResultsPageComponent } from './modules/my-documents/results-page/results-page.component';
import { TaggingComponent } from './modules/mypurchase-form/tagging/tagging.component';
import { TagItemVendorComponent } from './modules/mypurchase-form/tagging/tag-item-vendor/tag-item-vendor.component';
import { MatListModule, MatListOption } from '@angular/material/list';

import { AddExcoDocumentDialogComponent } from './modules/mypurchase-form/add-exco-document-dialog/add-exco-document-dialog.component';
import { SelectableTableComponent } from './shared/components/selectable-table/selectable-table.component';
import { AddReferenceDialogComponent } from './modules/mypurchase-form/add-reference-dialog/add-reference-dialog.component';
import { FilterDeletedPipe } from './shared/pipes/remove-soft-deleted-items.pipe';
import { AuditLogPageComponent } from './modules/shared/audit-log/audit-log.component';
import { GlobalBannerComponent } from './shared/components/global-banner/global-banner.component';
import { PurchasingExecutiveAssignmentFormDialogComponent } from './modules/approving-officer-page/purchasing-executive-assignment-form-dialog/purchasing-executive-assignment-form-dialog.component';
import { PurchasingExecutiveAssignmentListComponent } from './modules/approving-officer-page/purchasing-executive-assignment-list/purchasing-executive-assignment-list.component';
import { AuthInterceptor } from './auth/service/auth.interceptor';
import { currencyReducer } from './core/store/CurrencyExchange.reducer';
import { deletedItemVendorsReducer } from './core/store/DeletedItemVendors.reducer';
import { NoticeService } from './core/services/notice.service';
import { RemoveNonAsciiPipe } from './shared/pipes/remove-non-ascii.pipe';
import { attachmentReducer } from './core/store/attachment.reducer';
import { TippyDirective, popperVariation, provideTippyConfig, tooltipVariation } from '@ngneat/helipopper';
import { MyPurchaseFormComponent } from './modules/mypurchase-form/mypurchase-form.component';
import { PureAbility } from '@casl/ability';
import { AbilityModule, AbilityService } from '@casl/angular';
import { AppAbility, extractMySubjectType } from './core/services/permissions/AppAbility';
import { PermissionsService } from './core/services/permissions/permission.service';
import { UnauthorizedPageComponent } from './modules/unauthorized/unauthorized-page.component';
import { AppLayoutComponent } from './shared/components/app-layout/app-layout.component';
import { DroDialogComponent } from './modules/mypurchase-form/dro-dialog/dro-dialog.component';
import { ReportsPageComponent } from './modules/reports-page/reports-page.component';
import { RfsExtendLockDialogComponent } from './modules/mypurchase-form/rfs-extend-lock-dialog/rfs-extend-lock-dialog.component';
import { RfsInfoDialogComponent } from './modules/mypurchase-form/rfs-info-dialog/rfs-info-dialog.component';
import { AttachmentTaggingComponent } from './modules/mypurchase-form/attachments/attachment-tagging/attachment-tagging.component';
import { NotfoundPageComponent } from './modules/shared/notfound/notfound-page/notfound-page.component';
import { RfqDeadlineDialogComponent } from './modules/mypurchase-form/rfq-deadline-dialog/rfq-deadline-dialog.component';
import { RfqOutputComponent } from './modules/mypurchase-form/rfq-output/rfq-output.component';
import { ItemUploadTemplateComponent } from './modules/mypurchase-form/item-vendor/item-list/item-upload-template/item-upload-template.component';
import { SystemConfigurationService } from './core/services/systemConfiguration.service';
import { ValidationService } from './shared/services/validation.service';
import { CurrencyService } from './shared/services/currency.service';
import { RfsService } from './modules/mypurchase-form/rfs/rfs.service';
import { SelectTableDialogComponent } from './modules/mypurchase-form/rfs/select-table-dialog/select-table-dialog.component';
import { TaggingUpdateComponent } from './modules/mypurchase-form/rfs/tagging-update/tagging-update.component';
import { TaggingAddComponent } from './modules/mypurchase-form/rfs/tagging-add/tagging-add.component';
import { TaggingListComponent } from './modules/mypurchase-form/rfs/tagging-list/tagging-list.component';
import { TaggingValidationDialogComponent } from './modules/mypurchase-form/rfs/tagging-validation-dialog/tagging-validation-dialog.component';
import { TaggingValidationComponent } from './modules/mypurchase-form/rfs/tagging-validation/tagging-validation.component';

import { SelectItemTableComponent } from './shared/components/select-item-table/select-item-table.component';
import { SelectVendorTableComponent } from './shared/components/select-vendor-table/select-vendor-table.component';
import { RfqUpdateComponent } from './modules/mypurchase-form/rfq-update/rfq-update.component';
import { CapitalUsageRequirementsComponent } from './modules/mypurchase-form/item-vendor/item-list/add-item/capital-usage-requirements/capital-usage-requirements.component';
import { CapitalUsageReqSummaryComponent } from './modules/mypurchase-form/item-vendor/budget-summary/capital-usage-req-summary/capital-usage-req-summary.component';
import { GuideMeDialogComponent } from './modules/purchase-modes/guide-me/guide-me-dialog.component';
import { PeWorklistComponent } from './modules/pe-worklist/pe-worklist.component';
import { PeWorklistListingComponent } from './modules/pe-worklist/pe-worklist-listing/pe-worklist-listing.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { RfqSummaryComponent } from './modules/rfq-summary/rfq-summary.component';
import { SourcingOutcomeComponent } from './modules/rfq-summary/sourcing-outcome/sourcing-outcome.component';
import { VendorDocumentsComponent } from './modules/rfq-summary/vendor-documents/vendor-documents.component';
import { ItemsListComponent } from './modules/rfq-summary/items-list/items-list.component';
import { CreateRFQDialogComponent } from './shared/components/create-rfq-dialog/create-rfq-dialog.component';
import { QuotationListComponent } from './modules/rfq-summary/quotation-list/quotation-list.component';
import { ReassignDialogComponent } from './modules/rfq-summary/reassign-dialog/reassign-dialog.component';
import { CompleteDialogComponent } from './modules/rfq-summary/complete-dialog/complete-dialog.component';
import { ItemSelectionListingComponent } from './modules/rfq-summary/shared/item-selection-listing/item-selection-listing.component';
import { RaisePoDialogComponent } from './modules/rfq-summary/raise-po-dialog/raise-po-dialog.component';
import { ExcelUpdateDiologComponent } from './modules/rfq-summary/excel-upload-diolog/excel-upload-diolog.component';
import { RfqInfoDialogComponent } from './modules/rfq-summary/shared/rfq-info-dialog/rfq-info-dialog.component';
import { rfqSummaryReducer } from './core/store/rfqSummary.reducer';
import { RfqReportPageComponent } from './modules/rfq-report-page/rfq-report-page.component';
import { ContextMenuDirective } from './directives/context-menu.directive'; 
import { loadingReducer } from './core/store/loading.reducer';
import { TruncateMiddlePipe } from './shared/pipes/truncate-middle.pipe';
import { ConnectionLostComponent } from './modules/connection-lost/connection-lost.component';
import { SomethingWentWrongComponent } from './modules/something-went-wrong/something-went-wrong.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    AddExcoDocumentDialogComponent,
    SelectableTableComponent,
    AddReferenceDialogComponent,
    ResultsPageComponent,
    LandingPageComponent,
    OptionBoxComponent,
    OptionsSectionComponent,
    FormContainerComponent,
    FormHeaderComponent,
    FormStatusComponent,
    BasicInformationComponent,
    ItemVendorComponent,
    MyPurchaseFormComponent,
    AttachmentComponent,
    ReferenceComponent,
    AddReferenceCardComponent,
    BreadCrumbsComponent,
    DropdownComponent,
    RadioOptionsComponent,
    TextInputComponent,
    ButtonComponent,
    AddItemPageComponent,
    DialogComponent,
    GuideMeDialogComponent,
    ApprovingOfficerPageComponent,
    AoOverviewComponent,
    MyDocumentsListingComponent,
    WorklistPageComponent,
    MyDocumentsComponent,
    MyWorklistComponent,
    WorkflowHistoryPageComponent,
    AuditLogPageComponent,
    CurrentWorkflowComponent,
    LoaderComponent,
    LoaderDirective,
    ReadOnlyTableComponent,
    InfoDialogComponent,
    InputDialogComponent,
    AoActionDialogComponent,
    ForwardDialogComponent,
    FormTransformerPipe,
    BudgetSummaryComponent,
    AddVendorComponent,
    VendorSearchDialogComponent,
    ItemListComponent,
    ItemInfoComponent,
    ItemPriceComponent,
    ItemBudgetComponent,
    AddBudgetComponent,
    BudgetListingTableComponent,
    OutputHistoryComponent,
    AddendumDialogComponent,
    EmailDialogComponent,
    AslVendorSearchDialogComponent,
    EditVendorDialogComponent,
    ItemAddOnComponent,
    AddOnFormComponent,
    AddOnFeatureComponent,
    ExtendedWarrantyComponent,
    MaintenanceComponent,
    OptionToPurchaseComponent,
    TenderItemComponent,
    AddOnFeatureComponent,
    AddNewOptionPurchaseComponent,
    TenderDateTimeFormComponent,
    InputJustificationComponent,
    TenderDocumentsComponent,
    OptionPurchaseTableComponent,
    ScopeOfWorkComponent,
    CeDialogComponent,
    WorkflowTableComponent,
    CoveringOfficerChipComponent,
    EditableTableComponent,
    GlobalBannerComponent,
    TaggingComponent,
    TagItemVendorComponent,
    PurchasingExecutiveAssignmentFormDialogComponent,
    PurchasingExecutiveAssignmentListComponent,
    AlertComponent,
    UnauthorizedPageComponent,
    AppLayoutComponent,
    DroDialogComponent,
    ReportsPageComponent,
    RfsExtendLockDialogComponent,
    RfsInfoDialogComponent,
    AttachmentTaggingComponent,
    NotfoundPageComponent,
    RfqDeadlineDialogComponent,
    RfqOutputComponent,
    ItemUploadTemplateComponent,
    SelectItemTableComponent,
    SelectVendorTableComponent,
    SelectTableDialogComponent,
    TaggingUpdateComponent,
    TaggingAddComponent,
    TaggingListComponent,
    TaggingValidationDialogComponent,
    TaggingValidationComponent,
    RfqUpdateComponent,
    PeWorklistComponent,
    PeWorklistListingComponent,
    CapitalUsageRequirementsComponent,
    CapitalUsageReqSummaryComponent,
    RfqSummaryComponent,
    SourcingOutcomeComponent,
    VendorDocumentsComponent,
    ItemsListComponent,
    CreateRFQDialogComponent,
    QuotationListComponent,
    ReassignDialogComponent,
    CompleteDialogComponent,
    ItemSelectionListingComponent,
    RaisePoDialogComponent,
    ExcelUpdateDiologComponent,
    RfqInfoDialogComponent,
    RfqReportPageComponent,
    TruncateMiddlePipe,
    ContextMenuDirective,
    ConnectionLostComponent,
    SomethingWentWrongComponent,
  ],
  providers: [
    MatDatepickerModule,
    AbilityService,
    PermissionsService,
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-SG' },
    BackendApiService,
    SAPAPI,
    ShareDataService,
    //provideRouter(routes),
    FormTransformerPipe,
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
    { provide: AppAbility, useValue: new AppAbility([], { detectSubjectType: subject => extractMySubjectType(subject) }) },
    { provide: PureAbility, useExisting: AppAbility },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    SystemConfigurationService,
    NoticeService,
    ValidationService,
    CurrencyService,
    RfsService,
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
      }
    })
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatToolbarModule,
    MatButtonModule,
    NgbModule,
    MatCardModule,
    MatChipsModule,
    MatGridListModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatSidenavModule,
    MatTabsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatInputModule,
    MatButtonToggleModule,
    MatSlideToggleModule,
    MatSlideToggleModule,
    MatListModule,
    MatSnackBarModule,
    ReactiveFormsModule,
    FormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    AuthModule,
    MatExpansionModule,
    HttpClientModule,
    NgxFileDropModule,
    MatStepperModule,
    AbilityModule,
    StoreModule.forRoot({
      form: formReducer,
      dp: dropdownReducer,
      user: userReducer,
      tagging: taggingReducer,
      currency: currencyReducer,
      deletedItemVendors: deletedItemVendorsReducer,
      attachment: attachmentReducer,
      rfqSummary: rfqSummaryReducer,
      loading: loadingReducer 
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !isDevMode(),
      trace: true,
    }),
    NgSelectModule,
    FilterDeletedPipe,
    RemoveNonAsciiPipe,
    TippyDirective,
    DragDropModule,
    MatProgressBarModule
  ],
})
export class AppModule { }
